import {
  ChangeEvent,
  ElementType,
  FC,
  FocusEvent,
  InputHTMLAttributes,
  KeyboardEventHandler,
  MouseEventHandler,
  useRef,
  useState,
} from 'react'
import classNames from 'classnames'

import { IconProps } from '../../../icons/IconProps'
import { TextSmall, TextTiny } from '../../Typography/Typography'

import styles from './Input.module.scss'

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>
  onWrapperClick?: MouseEventHandler<HTMLDivElement>
  onStartIconClick?: MouseEventHandler<HTMLSpanElement>
  onEndIconClick?: MouseEventHandler<HTMLSpanElement>
  hint?: string
  decimalPlaces?: number
  maxDigits?: number
  zerosLimitter?: boolean
  lockOnLimit?: boolean
  min?: number
  max?: number
  label?: string
  state?: 'regular' | 'compact'
  isFullWidth?: boolean
  error?: boolean
  startIcon?: ElementType<IconProps>
  endIcon?: ElementType<IconProps>
}

export const Input: FC<InputProps> = (props) => {
  const {
    onChange,
    onWrapperClick,
    onStartIconClick,
    onEndIconClick,
    onBlur,
    onFocus,
    value,
    placeholder,
    decimalPlaces,
    maxDigits,
    zerosLimitter,
    lockOnLimit,
    min,
    max,
    hint,
    label,
    startIcon: StartIcon,
    endIcon: EndIcon,
    state = 'regular',
    type = 'text',
    isFullWidth,
    disabled,
    error,
    readOnly,
    ...restProps
  } = props

  const inputRef = useRef<HTMLInputElement>(null)

  const [isFocused, setIsFocused] = useState(false)

  const isCompact = state === 'compact'
  const isLabelShown =
    !!label?.length && !isCompact && (hasValue(value) || isFocused || disabled || error)

  const maxDecimalPlaces = new RegExp(`^\\d*\\.?\\d{0,${decimalPlaces}}$`)
  const startsWithMultipleZeros = new RegExp(`^0{2,}`)

  const validateNumberInput = (e: ChangeEvent<HTMLInputElement>) => {
    if (decimalPlaces !== undefined) {
      if (zerosLimitter) {
        if (startsWithMultipleZeros.test(e.target.value)) {
          return
        }
      }

      if (!maxDecimalPlaces.test(e.target.value)) {
        return
      }
    } else if (zerosLimitter) {
      if (startsWithMultipleZeros.test(e.target.value)) {
        return
      }
    }

    if (maxDigits !== undefined) {
      if (e.target.value.split('.')[0].length > maxDigits) {
        return
      }
    }

    if (isNaN(parseFloat(e.target.value))) {
      return onChange?.(e)
    }

    const numberValue = parseFloat(e.target.value)

    if (min !== undefined && numberValue < min) {
      if (lockOnLimit) {
        return
      }

      return onChange?.({ ...e, target: { ...e.target, value: min.toString() } })
    }

    if (max !== undefined && numberValue > max) {
      if (lockOnLimit) {
        return
      }

      return onChange?.({ ...e, target: { ...e.target, value: max.toString() } })
    }

    return onChange?.(e)
  }

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (disabled) {
      return
    }

    if (type === 'number') {
      return validateNumberInput(e)
    }

    return onChange?.(e)
  }

  const handleClick: MouseEventHandler<HTMLDivElement> = (event) => {
    if (disabled) {
      return
    }

    setIsFocused(true)
    inputRef.current?.focus()

    onWrapperClick?.(event)
  }

  const handleFocus = (event: FocusEvent<HTMLInputElement, Element>) => {
    setIsFocused(true)
    onFocus?.(event)
  }

  const handleBlur = (event: FocusEvent<HTMLInputElement, Element>) => {
    setIsFocused(false)
    onBlur?.(event)
  }

  return (
    <div
      className={classNames(styles.inputWrapper, {
        [styles.compact]: isCompact,
        [styles.fullWidth]: isFullWidth,
        [styles.disabled]: disabled,
        [styles.error]: error,
        [styles.focus]: isFocused,
        [styles.readOnly]: readOnly && disabled,
      })}
      onClick={handleClick}
    >
      <div className={styles.outerContainer}>
        {StartIcon && (
          <span className={styles.iconWrapper} onClick={onStartIconClick}>
            <StartIcon size={24} color={disabled ? 'foregroundDisabled' : 'foregroundSecondary'} />
          </span>
        )}
        <div className={classNames(styles.container, { [styles.disabled]: disabled })}>
          {isLabelShown && <TextTiny className={styles.inputLabel}>{label}</TextTiny>}
          <input
            {...restProps}
            ref={inputRef}
            value={value}
            onChange={handleChange}
            placeholder={isLabelShown ? placeholder : label || placeholder}
            type={type}
            disabled={disabled}
            readOnly={readOnly}
            data-placeholder={placeholder}
            onFocus={handleFocus}
            onBlur={handleBlur}
          />
        </div>
        {EndIcon && (
          <span className={styles.iconWrapper} onClick={onEndIconClick}>
            <EndIcon size={24} color={disabled ? 'foregroundDisabled' : 'foregroundSecondary'} />
          </span>
        )}{' '}
      </div>

      {!!hint?.length && <TextSmall className={styles.hint}>{hint}</TextSmall>}
    </div>
  )
}

const hasValue = (value: InputHTMLAttributes<HTMLInputElement>['value']): boolean =>
  value !== undefined && value !== ''
